import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import siteSettings from "../data/site-settings.json";
import { NAV } from "../constants/testIds";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Products", path: "/products" },
    { name: "Contact", path: "/contact" },
  ];

  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
        scrolled
          ? "bg-shadowWhite/95 backdrop-blur-xl shadow-sm border-b border-goldBorder"
          : "bg-shadowWhite/80 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group" data-testid={NAV.logo}>
            <img
              src={siteSettings.logo}
              alt={siteSettings.company_name}
              className="h-12 w-12 object-contain rounded-sm"
            />
            <div>
              <p className="font-cormorant text-lg font-bold text-deepCharcoal leading-tight tracking-wide">
                {siteSettings.company_name}
              </p>
              <p className="text-xs text-premiumGold tracking-[0.2em] uppercase font-outfit font-semibold">
                {siteSettings.brand}
              </p>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-outfit text-sm font-medium transition-colors duration-200 hover:text-premiumGold relative ${
                  isActive(link.path) ? "text-premiumGold" : "text-deepCharcoal"
                }`}
                data-testid={NAV[`link${link.name}`]}
              >
                {link.name}
                {isActive(link.path) && (
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-premiumGold rounded-full" />
                )}
              </Link>
            ))}
            <a
              href={`https://wa.me/${siteSettings.whatsapp}?text=${encodeURIComponent(
                "Hello AL ZAHRAN FLOUR MILL, I would like information about your products."
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-premiumGold text-deepCharcoal px-5 py-2.5 text-sm font-semibold font-outfit hover:bg-[#B8944A] transition-colors duration-200"
              data-testid={NAV.whatsappCta}
            >
              <Phone size={14} />
              <span>Order Now</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-deepCharcoal p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid={NAV.mobileMenuBtn}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden overflow-hidden bg-shadowWhite border-t border-goldBorder"
          >
            <div className="px-4 py-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block font-outfit text-sm font-medium py-3 px-2 border-b border-goldBorder/50 transition-colors duration-200 ${
                    isActive(link.path)
                      ? "text-premiumGold"
                      : "text-deepCharcoal hover:text-premiumGold"
                  }`}
                  data-testid={NAV[`mobile${link.name}`]}
                >
                  {link.name}
                </Link>
              ))}
              <a
                href={`https://wa.me/${siteSettings.whatsapp}?text=${encodeURIComponent(
                  "Hello AL ZAHRAN FLOUR MILL, I would like information about your products."
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-premiumGold text-deepCharcoal px-4 py-3 text-sm font-semibold font-outfit text-center mt-3"
                data-testid="mobile-nav-whatsapp"
              >
                Order on WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
